<template>
  <div ref="searchBar" class="w-full">
    <div class="flex gap-16">
      <ClientOnly fallbackTag="div">
        <Textbox
          ref="textbox"
          v-model="query"
          :input-id="'search'"
          :is-listbox-open="isDropdownOpen"
          :listbox-results-message="
            query
              ? i18n(translations.resultsListing, {
                  resultsCount: visibleProductsHits.length,
                  searchQuery: query,
                })
              : i18n(translations.textboxPlaceholder)
          "
          @submit="handleTextBoxSubmit"
        />

        <button
          v-show="isSearchBarActive"
          class="text-nowrap md:hidden"
          type="button"
          @click="closeSearchBar"
        >
          {{ i18n(translations.cancelLabel) }}
        </button>
        <template #fallback>
          <div
            class="bg-action-default-mid text-action-default-hi rounded-full h-[40px] w-full gap-4 pl-4 pr-8"
          ></div>
        </template>
      </ClientOnly>
    </div>

    <div
      class="fixed inset-x-0 top-[56px] md:absolute md:bottom-auto md:top-full md:-mt-48"
      :class="{
        'bottom-0 md:bottom-auto': isDropdownOpen,
      }"
      tabindex="-1"
    >
      <Transition
        enter-active-class="ease-out duration-100 md:duration-300 delay-[50ms] md:delay-0"
        enter-from-class="opacity-0 md:opacity-100 md:[clip-path:inset(0_0_100%)]"
        enter-to-class="opacity-100 md:[clip-path:inset(0_0_0%)]"
        leave-active-class="ease-in duration-0 md:duration-100"
        leave-from-class="opacity-100 md:[clip-path:inset(0_0_0%)]"
        leave-to-class="opacity-0 md:opacity-100 md:[clip-path:inset(0_0_100%)]"
      >
        <div
          v-show="isDropdownOpen"
          ref="searchBarDropdown"
          class="bg-surface-default-low border-static-default-low inset-0 h-full w-full grow overflow-y-auto overscroll-y-contain border-t md:absolute md:inset-auto md:h-auto"
          data-test="search-bar-dropdown"
          :tabindex="isTouchDevice ? 0 : undefined"
        >
          <div v-show="isSearchResultsVisible">
            <div
              v-show="!isFetchingProductAndSuggestions && !isTyping"
              class="flex flex-col-reverse gap-16 pt-24 md:flex-row"
            >
              <SuggestionsSection
                class="px-24 md:min-w-256 md:pb-40 md:pl-40 md:pr-0"
                :input-value="query"
                name="suggestions"
                :suggestions="suggestionsHits"
                :title="i18n(translations.suggestedSearches)"
                @select="handleSuggestionSectionSelect"
                @submit="handleSuggestionSectionSubmit"
              />
              <ProductSection
                class="grow px-24 md:px-0"
                :products="visibleProductsHits"
                @click-product="closeSearchBar"
              />
            </div>
            <Skeleton
              v-show="!(!isFetchingProductAndSuggestions && !isTyping)"
            />
          </div>
          <div
            v-show="!isSearchResultsVisible"
            class="mx-auto max-w-[650px] px-28 pb-40 pt-24"
          >
            <SuggestionsSection
              name="popularSearches"
              :suggestions="popularSearches"
              :title="i18n(translations.popularSearches)"
              @select="handleSuggestionSectionSelect"
              @submit="handleSuggestionSectionSubmit"
            />
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRouteLocationWithLocale } from '#imports'
import { computed, ref, useTemplateRef, watch } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import {
  type Fn,
  useEventListener,
  useFocusWithin,
  useMediaQuery,
  useScrollLock,
  useTimeoutFn,
} from '@vueuse/core'

import { ROUTES as PRODUCT_LIST_ROUTES } from '~/scopes/product-list/route-names'

import translations from './SearchBar.translations'
import ProductSection from './components/ProductSection/ProductSection.vue'
import Skeleton from './components/Skeleton/Skeleton.vue'
import SuggestionsSection from './components/SuggestionsSection/SuggestionsSection.vue'
import Textbox from './components/TextBox/Textbox.vue'
import { useAlgoliaSearch } from './composables/useAlgoliaSearch/useAlgoliaSearch'
import { useFetchPopularSearches } from './composables/useFetchPopularSearches/useFetchPopularSearches'
import { useFetchSearchBarConfiguration } from './composables/useFetchSearchBarConfiguration/useFetchSearchBarConfiguration'
import { useIsTyping } from './composables/useIsTyping/useIsTyping'
import { useToggleSearchBar } from './composables/useToggleSearchBar'

const i18n = useI18n()
const tracking = useTracking()
const resolveLocalizedRoute = useRouteLocationWithLocale()

const textboxRef = useTemplateRef('textbox')
const searchBarRef = useTemplateRef('searchBar')
const searchBarDropdownRef = useTemplateRef('searchBarDropdown')

const query = ref<string>('')
const isTouchDevice = ref(false)

const { openSearchBar, closeSearchBar, isSearchBarActive } =
  useToggleSearchBar()

const { configuration } = useFetchSearchBarConfiguration()
const { isTyping } = useIsTyping(query)
const {
  productsHits,
  suggestionsHits,
  isFetchingProductAndSuggestions,
  searchProductsAndSuggestions,
} = useAlgoliaSearch({ query, configuration })

const { data: popularSearches } = useFetchPopularSearches()

const breakpoint = useClientOnlyComputedBreakpoint()
const is2xl = useMediaQuery('(min-width: 1536px)')

const visibleProductsHits = computed(() => {
  if (is2xl.value) return productsHits.value.slice(0, 6)
  if (breakpoint.value >= Breakpoint.LG) return productsHits.value.slice(0, 5)
  if (breakpoint.value >= Breakpoint.MD) return productsHits.value.slice(0, 4)

  return productsHits.value.slice(0, 3)
})

const isSearchResultsVisible = computed(
  () =>
    query.value.length > 0 &&
    (productsHits.value.length > 0 ||
      suggestionsHits.value.length > 0 ||
      isFetchingProductAndSuggestions.value ||
      isTyping.value),
)

const isDropdownOpen = computed(
  () =>
    isSearchBarActive.value &&
    (isSearchResultsVisible.value || popularSearches.value?.length > 0),
)

watch(query, () => {
  if (query.value === '') {
    productsHits.value = []
    suggestionsHits.value = []
  }
})

watch(isTyping, () => {
  if (!isTyping.value) {
    searchProductsAndSuggestions()
  }
})

async function handleTextBoxSubmit() {
  const searchRoute = resolveLocalizedRoute({
    name: PRODUCT_LIST_ROUTES.SEARCH_RESULTS,
    query: {
      q: query.value,
    },
  })
  await navigateTo(searchRoute as RouteLocationRaw)
  tracking.trackFormSubmit({
    zone: 'header_searchbar',
    name: query.value,
    payload: {
      index_used: 0,
      search_type: 'suggested_models',
    },
  })
  query.value = ''
  closeSearchBar()
}

function handleSuggestionSectionSelect(title: string) {
  query.value = title
  textboxRef.value?.focusInput()
}

function handleSuggestionSectionSubmit() {
  closeSearchBar()
}

if (import.meta.client) {
  useEventListener(
    searchBarRef,
    'touchstart',
    () => {
      isTouchDevice.value = true
    },
    { once: true },
  )

  const { focused: isFocused } = useFocusWithin(searchBarRef)

  const { start: scheduleCloseSearchBar, stop: stopCloseSearchBar } =
    useTimeoutFn(closeSearchBar, 150)

  watch(isFocused, () => {
    if (isFocused.value) {
      stopCloseSearchBar()
      openSearchBar()
    } else {
      scheduleCloseSearchBar()
    }
  })

  const isPageScrollLocked = useScrollLock(document?.body)
  let stopTouchMoveListener: Fn

  watch(isDropdownOpen, (isOpen) => {
    isPageScrollLocked.value = isOpen

    if (isTouchDevice.value) {
      if (isOpen) {
        stopTouchMoveListener = useEventListener(
          searchBarDropdownRef,
          'touchmove',
          () => {
            searchBarDropdownRef.value?.focus()
          },
        )
      } else {
        stopTouchMoveListener?.()
      }
    }
  })
}
</script>
